import { matchesEpic } from "../epics/model.ts";
import type { Todo } from "../../domain/entities.ts";
import type { SavedView, ViewFilters } from "../../domain/app-types.ts";
import { dependencyReadiness, taskIsReady } from "../tasks/dependency-readiness.ts";
import { isCrossSpace, rootInView, matchesCustomFields } from "./view-scope.ts";

export interface SavedViewFilterContext {
  today(): string;
  addDays(date: string, days: number): string;
  now(): number;
  tags(todo: Todo): string[];
  sharedTaskIds(): ReadonlySet<string>;
  tasks(): Todo[];
  blockers(todo: Todo, tasks: Todo[]): Todo[];
}

export function defaultSavedViewFilters(): ViewFilters {
  return { statuses: ["open"], assigneeId: "", priorities: [], tags: [], excludedTags: [], tagMatch: "all", categoryId: "", due: "any", completedWithin: "any", sharing: "any", blocked: false, changedWithin: "any", epicId: "", cycleId: "", inactiveDays: 0, estimatedOnly: false, taskIds: [] };
}

export function savedViewsForRoot(views: SavedView[], rootId: string | null): SavedView[] {
  return views.filter(view => !view.rootId || view.rootId === rootId || !view.published && isCrossSpace(view.filters)).slice().sort((left, right) => (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER) || left.name.localeCompare(right.name));
}

export function canManageSavedView(view: SavedView, spaceOwner: boolean): boolean {
  return !view.published || spaceOwner;
}

export function canMoveSavedView(views: SavedView[], index: number, direction: "up" | "down", spaceOwner: boolean): boolean {
  const target = views[index], neighbor = views[index + (direction === "up" ? -1 : 1)];
  return !!target && !!neighbor && canManageSavedView(target, spaceOwner) && canManageSavedView(neighbor, spaceOwner);
}

export function defaultSavedView(views: SavedView[]): SavedView | undefined {
  return views.find(view => view.isDefault && !view.published) || views.find(view => view.isDefault && view.published);
}

export function matchesSavedViewFilters(todo: Todo, filters: ViewFilters, context: SavedViewFilterContext): boolean {
  if (!rootInView(todo.rootId, filters) || filters.collectionIds?.length && (!todo.categoryId || !filters.collectionIds.includes(todo.categoryId)) || !matchesCustomFields(todo, filters)) return false;
  if (filters.statuses.length && !filters.statuses.includes(todo.completed ? "completed" : "open")) return false;
  if (filters.assigneeId === "unassigned" && (todo.assignedTo || []).length) return false;
  if (filters.assigneeId && filters.assigneeId !== "unassigned" && !(todo.assignedTo || []).includes(filters.assigneeId)) return false;
  if (filters.priorities.length && !filters.priorities.includes(todo.priority)) return false;
  const tags = context.tags(todo);
  if (filters.tags.length && (filters.tagMatch === "any" ? !filters.tags.some(tag => tags.includes(tag)) : !filters.tags.every(tag => tags.includes(tag)))) return false;
  if (filters.excludedTags?.some(tag => tags.includes(tag))) return false;
  if (filters.categoryId === "none" && todo.categoryId) return false;
  if (filters.categoryId && filters.categoryId !== "none" && todo.categoryId !== filters.categoryId) return false;
  const today = context.today();
  if (filters.due === "today" && todo.dueDate !== today) return false;
  if (filters.due === "overdue" && (!todo.dueDate || todo.dueDate >= today || todo.completed)) return false;
  if (filters.due === "none" && todo.dueDate) return false;
  if (filters.due === "next7" && (!todo.dueDate || todo.dueDate < today || todo.dueDate > context.addDays(today, 7))) return false;
  if (filters.due === "next30" && (!todo.dueDate || todo.dueDate < today || todo.dueDate > context.addDays(today, 30))) return false;
  const now = context.now();
  if (filters.completedWithin === "week" && (!todo.completed || (todo.completedAt || todo.updatedAt) < now - 7 * 86_400_000)) return false;
  if (filters.sharing === "shared" && !context.sharedTaskIds().has(todo.id)) return false;
  if (filters.sharing === "private" && context.sharedTaskIds().has(todo.id)) return false;
  if (filters.ready && !taskIsReady(todo, context.tasks())) return false;
  if (filters.blocked && (todo.completed || !dependencyReadiness(todo, context.tasks()).unresolved.length)) return false;
  if (filters.changedWithin === "week" && todo.updatedAt < now - 7 * 86_400_000) return false;
  if (!matchesEpic(todo, context.tasks(), filters.epicId || "")) return false;
  if (filters.cycleId && todo.cycleId !== filters.cycleId) return false;
  if (filters.inactiveDays && todo.updatedAt > now - filters.inactiveDays * 86_400_000) return false;
  if (filters.estimatedOnly && !(todo.estimatedMinutes || 0)) return false;
  if (filters.taskIds?.length && !filters.taskIds.includes(todo.id)) return false;
  return true;
}
